import { Activity, LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { SkeletonLoader } from '../shared/SkeletonLoader';
import {
  useSessionMinutes,
  useSessionSensorUtilization,
} from '../../../hooks/analytics/useAnalytics';
import { useUtilizationOverview } from '../../../hooks/analytics/useUtilizationAnalytics';
import { useWellnessMetrics } from '../../../hooks/analytics/useWellnessAnalytics';
import { MetricCard } from './MetricCard';
import { TopListCard } from './TopListCard';
import { formatMinutes, formatPercent, formatNumber } from './format';
import { CurrentMonthLabel } from '../shared/CurrentMonthLabel';

interface CategoryPreviewProps {
  filters: BaseAnalyticsFilter;
  accent: string;
}

export function SessionPerformancePreview({ filters, accent }: CategoryPreviewProps) {
  const isMobile = useIsMobile();
  const sessionMinutes = useSessionMinutes(filters);
  const sensorUtilization = useSessionSensorUtilization(filters);

  const isLoading = sessionMinutes.isLoading || sensorUtilization.isLoading;
  const hasError = sessionMinutes.isError || sensorUtilization.isError;
  const hasDateFilter = Boolean(filters.endDate);

  if (isLoading) {
    return (
      <div className={`h-full ${isMobile ? 'p-3' : 'p-4'}`}>
        <SkeletonLoader variant="metrics" count={4} />
      </div>
    );
  }

  if (hasError || (!sessionMinutes.data && !sensorUtilization.data)) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-500">
        <div className="text-center">
          <Activity className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p>Unable to load session data</p>
        </div>
      </div>
    );
  }

  const utilizationRateAllTime = sensorUtilization.data?.sensorUtilizationRateAllTime || 0;
  const utilizationRateCurrentMonth =
    sensorUtilization.data?.sensorUtilizationRateCurrentMonth || 0;

  const formattedAllTimeDuration = formatMinutes(sessionMinutes.data?.totalMinutesAllTime || 0);
  const formattedCurrentMonthDuration = formatMinutes(
    sessionMinutes.data?.totalMinutesCurrentMonth || 0,
  );

  return (
    <div className={`h-full ${isMobile ? 'p-3' : 'p-4'}`}>
      <div className="grid h-full grid-cols-2 gap-3">
        <MetricCard
          value={formatNumber(sessionMinutes.data?.totalSessionsAllTime || 0)}
          label="Total Sessions"
          secondaryValue={
            hasDateFilter
              ? undefined
              : formatNumber(sessionMinutes.data?.totalSessionsCurrentMonth || 0)
          }
          secondaryLabel={hasDateFilter ? undefined : <CurrentMonthLabel />}
          accent={accent}
        />
        <MetricCard
          value={formattedAllTimeDuration}
          label="Total Minutes"
          secondaryValue={hasDateFilter ? undefined : formattedCurrentMonthDuration}
          secondaryLabel={hasDateFilter ? undefined : <CurrentMonthLabel />}
          accent={accent}
        />
        <MetricCard
          value={`${formatPercent(utilizationRateAllTime)}%`}
          label="Biosensor Utilization"
          secondaryValue={
            hasDateFilter ? undefined : `${formatPercent(utilizationRateCurrentMonth)}%`
          }
          secondaryLabel={hasDateFilter ? undefined : <CurrentMonthLabel />}
          accent={accent}
        />
        <MetricCard
          value={formatMinutes(sessionMinutes.computed?.averageSessionLengthAllTime || 0)}
          label="Avg Session Length (min)"
          secondaryValue={
            hasDateFilter
              ? undefined
              : formatMinutes(sessionMinutes.computed?.averageSessionLength || 0)
          }
          secondaryLabel={hasDateFilter ? undefined : <CurrentMonthLabel />}
          accent={accent}
        />
      </div>
    </div>
  );
}

export function UtilizationPreview({ filters, accent }: CategoryPreviewProps) {
  const isMobile = useIsMobile();
  const utilizationOverview = useUtilizationOverview(filters);
  const hasDateFilter = Boolean(filters.startDate || filters.endDate);

  if (utilizationOverview.isLoading) {
    return (
      <div className={`h-full ${isMobile ? 'p-3' : 'p-4'}`}>
        <SkeletonLoader variant="metrics" count={4} />
      </div>
    );
  }

  if (utilizationOverview.isError || !utilizationOverview.data) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-500">
        <div className="text-center">
          <Activity className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p>Unable to load utilization data</p>
        </div>
      </div>
    );
  }

  const data = utilizationOverview.data;
  const newUsersThisMonth = data.newUsersThisMonth;
  const returningUsersThisMonth = data.returningUsersThisMonth;
  const newUsersInPeriod = data.newUsersInPeriod;
  const returningUsersInPeriod = data.returningUsersInPeriod;
  const topUsersAllTime = data.topUsersAllTime;
  const topUsersThisMonth = data.topUsersThisMonth;

  return (
    <div className={`h-full ${isMobile ? 'p-3' : 'p-4'}`}>
      <div className="grid h-full grid-cols-2 gap-2">
        {hasDateFilter ? (
          <>
            <MetricCard
              value={formatNumber(newUsersInPeriod || 0)}
              label="New Members"
              accent={accent}
              className="px-2 [&>div>div:first-child]:mb-1"
            />
            <MetricCard
              value={formatNumber(returningUsersInPeriod || 0)}
              label="Returning Members"
              accent={accent}
              className="px-2 [&>div>div:first-child]:mb-1"
            />
          </>
        ) : (
          <>
            <MetricCard
              value={formatNumber(newUsersThisMonth)}
              label={
                <span className="text-[0.65rem] leading-tight">
                  New Members - <CurrentMonthLabel />
                </span>
              }
              accent={accent}
              className="px-1.5 [&>div>div:first-child]:mb-0 [&>div>div:first-child]:pr-5"
            />
            <MetricCard
              value={formatNumber(returningUsersThisMonth)}
              label={
                <span className="text-[0.65rem] leading-tight">
                  Returning Members - <CurrentMonthLabel />
                </span>
              }
              accent={accent}
              className="px-1.5 [&>div>div:first-child]:mb-0 [&>div>div:first-child]:pr-5"
            />
          </>
        )}
        <TopListCard
          title="Top 4 - Selected Period -"
          items={(topUsersAllTime || []).slice(0, 4)}
          label={u => `${u.first_name} ${u.last_name}`}
          value={u => formatNumber(u.total_sessions)}
          accent={accent}
          empty={{
            title: 'No all-time members yet',
            description: 'Rankings will appear here',
          }}
        />
        <TopListCard
          title={
            <span>
              Top 4 - <CurrentMonthLabel /> -
            </span>
          }
          items={(topUsersThisMonth || []).slice(0, 4)}
          label={u => `${u.first_name} ${u.last_name}`}
          value={u => formatNumber(u.sessions_this_month)}
          accent={accent}
          empty={{
            title: 'No monthly members yet',
            description: 'Rankings will appear here',
          }}
        />
      </div>
    </div>
  );
}

export function WellnessPreview({ filters, accent }: CategoryPreviewProps) {
  const isMobile = useIsMobile();
  const wellnessFilters = useMemo(
    () => ({
      startDate: filters.startDate,
      endDate: filters.endDate,
      experienceTitle: filters.experienceTitle,
      unitIds: filters.unitIds,
      experienceIds: filters.experienceIds,
    }),
    [
      filters.startDate?.getTime(),
      filters.endDate?.getTime(),
      filters.experienceTitle,
      filters.unitIds?.join(','),
      filters.experienceIds?.join(','),
    ],
  );

  const { data: metricsData, isLoading } = useWellnessMetrics(wellnessFilters);

  if (isLoading) {
    return (
      <div className={`h-full ${isMobile ? 'p-3' : 'p-4'}`}>
        <SkeletonLoader variant="metrics" count={4} />
      </div>
    );
  }

  if (!metricsData) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-500">
        <div className="text-center">
          <Activity className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p>Unable to load wellness data</p>
        </div>
      </div>
    );
  }

  const getMetricDisplay = (metricKey: keyof typeof metricsData) => {
    const metric = metricsData[metricKey];
    const config = {
      pleasant: { name: 'Emotional', accent },
      calm_excited: { name: 'Stress', accent },
      tense: { name: 'Tension', accent },
      energetic: { name: 'Physical', accent },
    };

    return {
      name: config[metricKey].name,
      accent: config[metricKey].accent,
      improvement: metric.improvement_percentage,
      preAvg: metric.avg_pre,
      postAvg: metric.avg_post,
    };
  };

  const emotional = getMetricDisplay('pleasant');
  const stress = getMetricDisplay('calm_excited');
  const tension = getMetricDisplay('tense');
  const physical = getMetricDisplay('energetic');

  const formatImprovement = (pct: number | null) =>
    pct !== null ? `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%` : 'N/A';
  const whenDesktop = <T,>(v: T): T | undefined => (isMobile ? undefined : v);

  return (
    <div className={`h-full ${isMobile ? 'p-3' : 'p-4'}`}>
      <div className="grid h-full grid-cols-2 gap-3">
        {[emotional, stress, tension, physical].map(metric => (
          <MetricCard
            key={metric.name}
            value={metric.name}
            label="Wellness Metric"
            secondaryValue={whenDesktop(formatImprovement(metric.improvement))}
            secondaryLabel={whenDesktop('Improvement')}
            accent={metric.accent}
          />
        ))}
      </div>
    </div>
  );
}

export function SystemPerformancePreview({ accent }: CategoryPreviewProps) {
  const isMobile = useIsMobile();
  return (
    <div className={`h-full ${isMobile ? 'p-3' : 'p-4'}`}>
      <div className="grid h-full grid-cols-2 gap-3">
        <MetricCard value="--" label="Uptime" accent={accent} />
        <MetricCard value="--" label="Response Time" accent={accent} />
        <div className="relative col-span-2 rounded-b-none rounded-t-lg border border-[var(--anl-border)] bg-[var(--anl-metric-bg)] p-3 pb-4">
          <div className="mb-1 text-lg font-bold text-[var(--anl-text)]">Coming Soon</div>
          <div className="text-xs font-medium text-[var(--anl-text-muted)]">System Health</div>
          <div
            className="absolute bottom-0 left-0 right-0"
            style={{
              height: 6,
              backgroundColor: accent,
              borderRadius: '0 0 6px 6px',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function ComingSoonPreview({
  icon: IconComponent,
  title,
  accent,
}: {
  icon: LucideIcon;
  title: string;
  accent?: string;
}) {
  return (
    <div className="flex h-full items-center justify-center text-gray-400">
      <div className="text-center">
        <IconComponent className="mx-auto mb-3 h-12 w-12" style={{ color: accent || '#9ca3af' }} />
        <p className="mb-1 text-sm font-medium text-gray-500">{title}</p>
        <p className="text-xs text-gray-400">Coming Soon</p>
      </div>
    </div>
  );
}
